/**
 * Capacity-specific hooks using TanStack Query
 * Provides data fetching and caching for capacity utilization operations
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { keepPreviousDataForOrg } from '@/lib/tenantPlaceholder';
import { CapacityService } from '@/api/services/CapacityService';
import type {
  GroupUtilizationResponse,
  GroupUtilizationRangeResponse,
  OrgCapacityOverviewResponse,
  OrgCapacityRangeResponse,
  ChildCategorizationResponse,
  CategorizationListResponse,
  ServiceBreakdownResponse,
} from '@/types/capacity';

/**
 * Optional filters for the capacity child search.
 * - includeInactive: also return children with no current/upcoming contract
 * - ageMinMonths / ageMaxMonths: inclusive age bounds in total months
 * - ageAsOf: reference date (YYYY-MM-DD) the age bounds are evaluated against
 */
export interface CapacityChildSearchFilter {
  includeInactive: boolean;
  ageMinMonths?: number;
  ageMaxMonths?: number;
  ageAsOf?: string;
}

// Query keys for capacity
export const capacityKeys = {
  all: ['capacity'] as const,
  groups: () => [...capacityKeys.all, 'groups'] as const,
  groupUtilization: (groupId: string, date?: string) =>
    [...capacityKeys.groups(), groupId, 'utilization', { date }] as const,
  groupUtilizationRange: (groupId: string, from: string, to: string) =>
    [...capacityKeys.groups(), groupId, 'utilization-range', { from, to }] as const,
  serviceBreakdown: (groupId: string, date: string) =>
    [...capacityKeys.groups(), groupId, 'services', { date }] as const,
  // orgId is part of the key at index 2: an orgId-less key could serve the
  // previous tenant's grid on first paint after an org switch, and
  // keepPreviousDataForOrg reads that slot to keep the placeholder tenant-scoped.
  overview: (orgId: string, date?: string, includeServices?: boolean) =>
    [...capacityKeys.all, 'overview', orgId, { date, includeServices: !!includeServices }] as const,
  // orgId MUST sit at index 2 — keepPreviousDataForOrg reads queryKey[2] to keep the
  // quarter heatmap's placeholder tenant-scoped (so quarter navigation doesn't blank),
  // and useCapacityQueryKeys.test.ts pins that slot. A single 'overview-range' segment
  // keeps orgId at index 2; the object carries every varying arg so a stale key can never
  // serve another tenant/range's heatmap.
  overviewRange: (
    orgId: string,
    from: string,
    to: string,
    granularity: string,
    location?: string,
    groupId?: string,
  ) =>
    [...capacityKeys.all, 'overview-range', orgId,
      { from, to, granularity, location, groupId }] as const,
  children: () => [...capacityKeys.all, 'children'] as const,
  childSync: (childId: string) => [...capacityKeys.children(), childId, 'sync'] as const,
  categorization: (limit?: number, offset?: number) =>
    [...capacityKeys.children(), 'categorization', { limit, offset }] as const,
  categorizationSearch: (search: string, filter?: CapacityChildSearchFilter) =>
    [...capacityKeys.children(), 'categorization', 'search', { search, ...filter }] as const,
  outOfSync: (limit?: number, offset?: number) =>
    [...capacityKeys.children(), 'out-of-sync', { limit, offset }] as const,
  // orgId sits at index 2, the slot keepPreviousDataForOrg reads — see overview() above.
  attention: (orgId: string, location?: string, horizonDays?: number) =>
    [...capacityKeys.all, 'attention', orgId, { location, horizonDays }] as const,
  // orgId at index 2, same slot convention as overview()/attention().
  recentMoves: (orgId: string, days?: number, mine?: boolean, limit?: number, offset?: number) =>
    [...capacityKeys.all, 'recent-moves', orgId, { days, mine, limit, offset }] as const,
};

// Hook to fetch group utilization for a specific date
export function useGroupUtilization(groupId: string, date?: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.groupUtilization(groupId, date),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityGroupsUtilization(groupId, date);
      return response as GroupUtilizationResponse;
    },
    enabled: !!groupId && isSignedIn && isAuthReady,
    staleTime: 2 * 60 * 1000, // 2 minutes (capacity changes more frequently)
    retry: (failureCount, error) => {
      // Don't retry on 401/403/404 errors
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('404') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch group utilization for a date range
export function useGroupUtilizationRange(groupId: string, from: string, to: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.groupUtilizationRange(groupId, from, to),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityGroupsUtilizationRange(groupId, from, to);
      return response as GroupUtilizationRangeResponse;
    },
    enabled: !!groupId && !!from && !!to && isSignedIn && isAuthReady,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('404') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch organization capacity overview
export function useOrgCapacityOverview(
  date?: string,
  includeServices?: boolean,
  options?: { enabled?: boolean },
) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.overview(orgId || '', date, includeServices),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityOverview(date, includeServices);
      return response as OrgCapacityOverviewResponse;
    },
    // Keep the previous period's grid on screen while the next one loads —
    // only within the same tenant; an org switch resolves to a real loading state.
    placeholderData: keepPreviousDataForOrg<OrgCapacityOverviewResponse>(orgId),
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady && !!orgId,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch the org-wide, weeks-granular capacity range that backs the quarter
// heatmap (C23). One request returns per-group, per-week aggregates for the whole
// quarter — the org-wide sibling of useGroupUtilizationRange, with no per-group N+1.
export function useCapacityRange(
  from: string,
  to: string,
  granularity = 'week',
  options?: { location?: string; groupId?: string; enabled?: boolean },
) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.overviewRange(
      orgId || '',
      from,
      to,
      granularity,
      options?.location,
      options?.groupId,
    ),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityOverviewRange(
        from,
        to,
        granularity,
        options?.location,
        options?.groupId,
      );
      return response as OrgCapacityRangeResponse;
    },
    // Keep the previous quarter's heatmap on screen while the next one loads —
    // only within the same tenant; an org switch resolves to a real loading state.
    placeholderData: keepPreviousDataForOrg<OrgCapacityRangeResponse>(orgId),
    enabled:
      (options?.enabled ?? true) && isSignedIn && isAuthReady && !!orgId && !!from && !!to,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on 401/403/404 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('404') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch child sync status
export function useChildSyncStatus(childId: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.childSync(childId),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityChildrenSyncStatus(childId);
      return response as ChildCategorizationResponse;
    },
    enabled: !!childId && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('404') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch children categorization (simple query)
export function useChildrenCategorization(limit = 50, offset = 0) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.categorization(limit, offset),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityChildrenCategorization(limit, offset);
      return response as CategorizationListResponse;
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch children categorization with infinite scroll and server-side search
// Supports search by name (firstname/lastname) or internal ID (including leading zeros),
// plus optional include-inactive and age-range filters.
export function useChildrenCategorizationInfinite(
  search: string,
  pageSize = 50,
  filter?: CapacityChildSearchFilter
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useInfiniteQuery({
    queryKey: capacityKeys.categorizationSearch(search, filter),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await CapacityService.getApiV1CapacityChildrenCategorization(
        pageSize,
        pageParam,
        search || undefined,
        filter?.includeInactive,
        filter?.ageMinMonths,
        filter?.ageMaxMonths,
        filter?.ageAsOf
      );
      return response as CategorizationListResponse;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // Calculate total loaded items
      const totalLoaded = allPages.reduce(
        (sum, page) => sum + (page.children?.length ?? 0),
        0
      );
      // Return next offset if there are more items
      if (totalLoaded < (lastPage.total ?? 0)) {
        return totalLoaded;
      }
      return undefined; // No more pages
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch out-of-sync children
export function useOutOfSyncChildren(limit = 50, offset = 0) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.outOfSync(limit, offset),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityChildrenOutOfSync(limit, offset);
      return response as CategorizationListResponse;
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch service breakdown for a group on a specific date
// Lazy loads by default - only fetches when explicitly enabled (e.g., on hover/click)
export function useServiceBreakdown(
  groupId: string,
  date: string,
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.serviceBreakdown(groupId, date),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityGroupsUtilizationServices(groupId, date);
      return response as ServiceBreakdownResponse;
    },
    enabled: options?.enabled !== undefined
      ? options.enabled && !!groupId && !!date && isSignedIn && isAuthReady
      : false, // Default: disabled for lazy loading
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('404') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}
